import "./styles.scss";

import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import classnames from "classnames";

const AnimatedGradient = ({
  id,
  time,
  seed,
  colors,
  pixelRatio = 0.5,
  isMouseDisabled,
}) => {
  const ref = useRef(null);
  const [isInit, setIsInit] = useState(false);

  useEffect(() => {
    const container = ref.current;
    const canvas = document.getElementById(id);

    if (!container || !canvas || (!colors && !Array.isArray(colors))) return;

    let animationId = null;
    let resizeDebounce = null;
    let lastTimestamp = performance.now();
    let lastWindowWidth = window.innerWidth;

    const computeDimensions = () => {
      const { clientWidth, clientHeight } = container;
      const minWidth = 720;
      const maxWidth = 1920;
      const width = Math.max(minWidth, Math.min(maxWidth, clientWidth));
      const aspectRatio = clientWidth < 360 ? 1 : clientWidth / clientHeight;
      const height = width / aspectRatio;
      const segmentsX = Math.min(32, Math.ceil(width * 0.08));
      const segmentsY = Math.min(18, Math.ceil(height * 0.08));

      return { width, height, segmentsX, segmentsY };
    };

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      precision: "highp",
      powerPreference: "high-performance",
    });

    const { width, height, segmentsX, segmentsY } = computeDimensions();

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, pixelRatio));
    renderer.setSize(width, height, false);
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();

    const camera = new THREE.OrthographicCamera(
      width / -2,
      width / 2,
      height / 2,
      height / -2,
      1,
      2000,
    );
    camera.position.z = 20;

    const uniforms = {
      uTime: { value: time ?? 0 },
      uSeed: { value: seed ?? 5 },
      uAmp: { value: 320 },
      uFreqX: { value: 14e-5 },
      uFreqY: { value: 27e-5 },
      uResolution: { value: new THREE.Vector2(width, height) },
      uColors: {
        value: colors.map((color) => new THREE.Color(color)),
      },
      uMouse: { value: new THREE.Vector2(0.5, 0.5) },
      uMouseIntensity: { value: 0.0 },
    };

    const noiseShader = `
      vec3 mod289(vec3 x) { 
        return x - floor(x * (1.0 / 289.0)) * 289.0; 
      }
      vec4 mod289(vec4 x) { 
        return x - floor(x * (1.0 / 289.0)) * 289.0; 
      }
      vec4 permute(vec4 x) { 
        return mod289(((x * 34.0) + 1.0) * x); 
      }
      vec4 taylorInvSqrt(vec4 r) { 
        return 1.79284291400159 - 0.85373472095314 * r; 
      }
      
      float snoise(vec3 v) {
        const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
        
        vec3 i = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);
        
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);
        
        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;
        
        i = mod289(i);
        vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          
        float n_ = 0.142857142857;
        vec3 ns = n_ * D.wyz - D.xzx;
        
        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
        
        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);
        
        vec4 x = x_ * ns.x + ns.yyyy;
        vec4 y = y_ * ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);
        
        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);
        
        vec4 s0 = floor(b0) * 2.0 + 1.0;
        vec4 s1 = floor(b1) * 2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));
        
        vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
        
        vec3 p0 = vec3(a0.xy, h.x);
        vec3 p1 = vec3(a0.zw, h.y);
        vec3 p2 = vec3(a1.xy, h.z);
        vec3 p3 = vec3(a1.zw, h.w);
        
        vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        
        vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
        m = m * m;

        return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
      }
    `;

    const blendShader = `
      vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
        return (blend * opacity + base * (1.0 - opacity));
      }
    `;

    const vertexShader = `
      precision highp float;

      uniform vec2 uResolution;
      uniform float uTime;
      uniform float uSeed;
      uniform float uAmp;
      uniform float uFreqX;
      uniform float uFreqY;
      uniform vec2 uMouse;
      uniform float uMouseIntensity;
      
      varying vec2 vUv;
      
      ${noiseShader}
      
      void main() {
        vUv = uv;
        
        vec2 uvNorm = uv;
        uvNorm.y = 1.0 - uvNorm.y;
        
        float time = uTime * 0.0075;
        vec2 noiseCoord = uResolution * uvNorm * vec2(uFreqX, uFreqY);
        
        float distanceToMouse = distance(uvNorm, uMouse);
        float mouseInfluence = (1.0 - smoothstep(0.0, 1.0, distanceToMouse)) * uMouseIntensity;
        
        float adjustedNoiseFlow = 2.0 + mouseInfluence * 0.1;
        
        float noiseFlow = adjustedNoiseFlow;
        float noiseSpeed = 10.0;
        
        vec3 noisePos = vec3(
          noiseCoord.x * 3.0 + noiseFlow,
          noiseCoord.y * 4.0,
          time * noiseSpeed + uSeed
        );
        noisePos.xy += (uMouse - vec2(0.5)) * mouseInfluence * 0.2;
        
        float noise = snoise(noisePos) * uAmp;
        float mouseWave = sin(distanceToMouse * 10.0) * mouseInfluence * uAmp * 0.05;
        noise += mouseWave;
        noise *= 1.0 - pow(abs(uvNorm.y - 0.5) * 2.0, 2.0);
        noise = max(0.0, noise);
        
        vec3 pos = vec3(
          position.x,
          position.y + noise, 
          position.z
        );
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `;

    const fragmentShader = `
      precision highp float;

      uniform vec2 uResolution;
      uniform float uTime;
      uniform float uSeed;
      uniform float uAmp;
      uniform float uFreqX;
      uniform float uFreqY;
      uniform vec3 uColors[4];
      uniform vec2 uMouse;
      uniform float uMouseIntensity;
      
      varying vec2 vUv;
      
      ${noiseShader}
      ${blendShader}
      
      void main() {
        float time = uTime * 0.0075;

        vec2 uvNorm = vUv;
        uvNorm.y = 1.0 - uvNorm.y;
        
        float distanceToMouse = distance(uvNorm, uMouse);
        float mouseInfluence = (1.0 - smoothstep(0.0, 0.75, distanceToMouse)) * uMouseIntensity;
        
        vec2 noiseCoord = uResolution * (uvNorm + (uMouse - 0.5) * 0.1 * mouseInfluence) * vec2(uFreqX, uFreqY);
        
        vec3 color = uColors[0];

        for (int i = 0; i < 3; i++) {
          int step = i + 1;

          float noise = snoise(vec3(
            noiseCoord.x * (2.0 + float(step) / 4.0) + time * (6.5 + 0.3 * float(step)),
            noiseCoord.y * (3.0 + float(step) / 4.0),
            time * (11.0 + 0.3 * float(step)) + (uSeed + 10.0 * float(step))
          )) / 2.0 + 0.5;

          noise = smoothstep(0.1, 0.63 + 0.07 * float(step), noise);
          color = blendNormal(color, uColors[step], pow(noise, 4.0));
        }
      
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader,
      fragmentShader,
      transparent: true,
    });

    const geometry = new THREE.PlaneGeometry(
      width,
      height,
      segmentsX,
      segmentsY,
    );
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(0, 0, 0);
    scene.add(mesh);

    const animate = (timestamp) => {
      const deltaTime = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      uniforms.uTime.value += deltaTime;
      renderer.render(scene, camera);

      animationId = requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (animationId) return;
      lastTimestamp = performance.now();
      animationId = requestAnimationFrame(animate);
    };

    const stopAnimation = () => {
      if (!animationId) return;
      cancelAnimationFrame(animationId);
      animationId = null;
    };

    const handleResize = () => {
      if (window.innerWidth === lastWindowWidth) return;
      lastWindowWidth = window.innerWidth;

      clearTimeout(resizeDebounce);

      resizeDebounce = setTimeout(() => {
        const { width, height, segmentsX, segmentsY } = computeDimensions();

        renderer.setPixelRatio(Math.min(window.devicePixelRatio, pixelRatio));
        renderer.setSize(width, height, false);

        camera.left = width / -2;
        camera.right = width / 2;
        camera.top = height / 2;
        camera.bottom = height / -2;
        camera.updateProjectionMatrix();

        mesh.geometry.dispose();
        mesh.geometry = new THREE.PlaneGeometry(
          width,
          height,
          segmentsX,
          segmentsY,
        );

        uniforms.uResolution.value.set(width, height);
      }, 200);
    };

    const handleMouseMove = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;

      const currentX = uniforms.uMouse.value.x;
      const currentY = uniforms.uMouse.value.y;

      uniforms.uMouse.value.x = currentX + (x - currentX) * 0.02;
      uniforms.uMouse.value.y = currentY + (y - currentY) * 0.02;

      uniforms.uMouseIntensity.value = Math.min(
        0.6,
        uniforms.uMouseIntensity.value + 0.01,
      );
    };

    animationId = requestAnimationFrame(animate);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) startAnimation();
        else stopAnimation();
      },
      { threshold: 0 },
    );
    observer.observe(container);

    window.addEventListener("resize", handleResize);

    if (!isMouseDisabled) {
      window.addEventListener("mousemove", handleMouseMove);
    }

    setIsInit(true);

    return () => {
      stopAnimation();
      observer.disconnect();

      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
        scene.remove(mesh);
      }

      if (renderer) {
        renderer.dispose();
      }

      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);

      clearTimeout(resizeDebounce);
    };
  }, [id, time, seed, colors, pixelRatio, isMouseDisabled]);

  return (
    <div
      className={classnames("animated-gradient", {
        "animated-gradient--init": isInit,
      })}
      ref={ref}
    >
      <canvas id={id} />
    </div>
  );
};

export default AnimatedGradient;
